import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Calendar, User, ArrowLeft, Tag, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPostPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/blog/${postId}`);
        setPost(response.data);
      } catch (error) {
        console.error("Error fetching blog post:", error);
        navigate("/blog");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [postId, navigate]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div data-testid="blog-post-page" className="min-h-screen bg-gray-50">
      {/* Header Image */}
      <div className="h-64 md:h-96 relative">
        <img 
          src={post.image_url} 
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A5F]/80 to-transparent" />
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 -mt-32 relative z-10">
        <article className="bg-white rounded-xl shadow-xl overflow-hidden">
          <div className="p-8 md:p-12">
            {/* Back Button */}
            <Link 
              to="/blog" 
              className="inline-flex items-center text-gray-500 hover:text-[#1E3A5F] mb-6 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Link>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#9D9045]/10 text-[#9D9045] rounded-full text-sm font-medium">
                <Tag className="h-3 w-3" />
                {post.category}
              </span>
              <span className="inline-flex items-center gap-1 text-sm text-gray-500">
                <Calendar className="h-4 w-4" />
                {formatDate(post.created_at)}
              </span>
              <span className="inline-flex items-center gap-1 text-sm text-gray-500">
                <User className="h-4 w-4" />
                {post.author}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-[#1E3A5F] mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
              {post.title}
            </h1>

            {/* Content */}
            <div className="prose prose-lg max-w-none prose-headings:font-semibold prose-headings:text-[#1E3A5F] prose-p:text-gray-600 prose-li:text-gray-600 prose-strong:text-[#1E3A5F]">
              {post.content.split('\n').map((paragraph, i) => {
                if (paragraph.startsWith('## ')) {
                  return <h2 key={i} className="text-2xl font-semibold text-[#1E3A5F] mt-8 mb-4">{paragraph.replace('## ', '')}</h2>;
                } else if (paragraph.startsWith('### ')) {
                  return <h3 key={i} className="text-xl font-semibold text-[#1E3A5F] mt-6 mb-3">{paragraph.replace('### ', '')}</h3>;
                } else if (paragraph.startsWith('- **')) {
                  const match = paragraph.match(/- \*\*(.+?)\*\*: (.+)/);
                  if (match) {
                    return (
                      <div key={i} className="flex items-start gap-3 my-3">
                        <span className="h-2 w-2 bg-[#9D9045] rounded-full mt-2 flex-shrink-0" />
                        <p className="text-gray-600">
                          <strong className="text-[#1E3A5F]">{match[1]}</strong>: {match[2]}
                        </p>
                      </div>
                    );
                  }
                } else if (paragraph.startsWith('- ')) {
                  return (
                    <div key={i} className="flex items-start gap-3 my-2">
                      <span className="h-2 w-2 bg-[#9D9045] rounded-full mt-2 flex-shrink-0" />
                      <p className="text-gray-600">{paragraph.replace('- ', '')}</p>
                    </div>
                  );
                } else if (paragraph.trim()) {
                  return <p key={i} className="text-gray-600 my-4 leading-relaxed">{paragraph}</p>;
                }
                return null;
              })}
            </div>

            {/* CTA */}
            <div className="mt-12 pt-8 border-t border-gray-100">
              <div className="bg-[#1E3A5F]/5 rounded-xl p-8 text-center">
                <h3 className="text-xl font-semibold text-[#1E3A5F] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Ready to Get Started?
                </h3>
                <p className="text-gray-600 mb-6">
                  At OMW Business & Financial Resources, we guide you through every step of building your business.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/contact">
                    <Button className="bg-[#1E3A5F] hover:bg-[#162c47] text-white px-6" data-testid="post-contact-btn">
                      Schedule Free Consultation
                    </Button>
                  </Link>
                  <Link to="/school">
                    <Button variant="outline" className="border-[#9D9045] text-[#9D9045] hover:bg-[#9D9045] hover:text-white px-6" data-testid="post-school-btn">
                      Explore Courses
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>

      {/* Spacer */}
      <div className="h-20" />
    </div>
  );
};

export default BlogPostPage;
